import { useState, useEffect } from 'react';
import { Modal } from '@carbon/react';
import { DatePicker } from 'components/date-picker/date-picker';
import moment from 'moment';
import styles from './edit-cost-date-modal.module.scss';

interface EditCostDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (dateValue: string) => void;
  slotId: string;
  wellName?: string;
  dateFieldLabel: string;
  currentDate?: string;
  /**
   * Inclusive ISO-day (`YYYY-MM-DD`) lifecycle-order bounds. Supplied by callers that edit a
   * schedule milestone, from the SAME `neighborBounds()` the schedule dialog clamps with, so
   * the picker cannot offer a day `OverrideWriteValidator` would reject at Calculate.
   * INCLUSIVE on both sides, matching the validator's `predecessor > written` /
   * `successor < written` predicate — the boundary day itself is legal.
   * Omitted by callers with no ordering constraint (the AFE cost dates).
   */
  minDate?: string;
  maxDate?: string;
  /**
   * The neighbour each bound comes from, for the refusal message. A range without a name
   * ("must be on or after 04/27/2026") makes the user hunt for which row they have crossed;
   * A bare rejection on a completion-date edit was reported in UAT once already.
   * Omitted by callers with no ordering constraint.
   */
  minLabel?: string;
  maxLabel?: string;
}

/**
 * Normalizes a date string to YYYY-MM-DD format.
 * Accepts both YYYY-MM-DD and MM/DD/YYYY inputs.
 * @param {string} dateStr - The date string to normalize.
 * @return {string | null} The normalized date or null if invalid.
 */
const normalizeDate = (dateStr: string): string | null => {
  if (!dateStr) return null;
  const m = moment(dateStr, ['YYYY-MM-DD', 'MM/DD/YYYY'], true);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

/**
 * Turns an ISO day into the LOCAL midnight Date flatpickr compares calendar days against.
 * Passing the bare `YYYY-MM-DD` string would be parsed with the picker's own `m/d/Y`
 * `dateFormat` and land on the wrong day; a Date is unambiguous.
 *
 * @param {string} iso - The ISO day.
 * @return {Date} Local midnight on that day.
 */
const pickerBound = (iso: string): Date => new Date(`${iso}T00:00:00`);

/** The refusal message element's id. */
const MESSAGE_ID = 'cost-date-picker-message';

/**
 * An ISO day as the page writes dates, for the refusal message.
 *
 * @param {string} iso - The ISO day.
 * @return {string} `MM/DD/YYYY`.
 */
const formatDay = (iso: string): string =>
  moment(iso, 'YYYY-MM-DD').format('MM/DD/YYYY');

/**
 * Whether an ISO day falls outside its lifecycle-order bounds.
 *
 * EXPORTED SO THE RULE CAN BE TESTED DIRECTLY. flatpickr does not attach in jsdom, so nothing
 * can drive the picker's state from a test — leaving the comparison itself unreachable and
 * two mutants of it alive. It is the same comparison the component applies; there is no
 * second rule, only a name for the one that was inline.
 *
 * INCLUSIVE on both sides, matching OverrideWriteValidator's `predecessor > written` /
 * `successor < written`: the boundary day is legal. An absent bound constrains nothing — a
 * row with no neighbour on that side has no order to violate.
 *
 * @param {string} [selected] - The chosen ISO day.
 * @param {string} [minDate] - Inclusive lower bound, if any.
 * @param {string} [maxDate] - Inclusive upper bound, if any.
 * @return {boolean} True when the choice breaks lifecycle order.
 */
export const violatesOrder = (
  selected?: string,
  minDate?: string,
  maxDate?: string
): boolean =>
  selected !== undefined &&
  ((minDate !== undefined && selected < minDate) ||
    (maxDate !== undefined && selected > maxDate));

/**
 * Modal for editing a single cost date field on a slot.
 * Uses a DatePicker, pre-fills with the current value, and saves on change.
 * @return {JSX.Element} The edit cost date modal component.
 */
export const EditCostDateModal = ({
  isOpen,
  onClose,
  onSave,
  slotId,
  wellName,
  dateFieldLabel,
  currentDate,
  minDate,
  maxDate,
  minLabel,
  maxLabel,
}: EditCostDateModalProps) => {
  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    currentDate ? (normalizeDate(currentDate) ?? undefined) : undefined
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedDate(
        currentDate ? (normalizeDate(currentDate) ?? undefined) : undefined
      );
    }
  }, [isOpen, currentDate]);

  const normalizedCurrent = currentDate
    ? normalizeDate(currentDate)
    : undefined;
  const hasChanged = selectedDate !== normalizedCurrent;

  // ── THE ORDER IS CHECKED ON SAVE, NOT ONLY IN THE CALENDAR ──────────────────────────────
  //
  // `minDate`/`maxDate` reach flatpickr and grey out the days outside the range, and that is
  // where the clamp used to end: #handleSave took whatever was in state. A greyed calendar is
  // the only thing standing between an out-of-order date and the pending log, and it is not
  // the only way a value gets into that state — which is why a completion date before its
  // permit date was accepted on v6476.
  //
  // THE SAME BOUNDS, NOT A SECOND RULE. Both come from the one `neighborBounds()` the caller
  // already computed; this only stops the modal from ignoring them. Inclusive on both sides,
  // matching the validator's own predicate, so the boundary day is legal.
  //
  // A date outside the range is what OverrideWriteValidator refuses at Calculate with a bare
  // 422 (calculations_controller.rb:413). Saying so here, next to the field, is the whole
  // point: the user learns which neighbour they crossed while they can still fix it.
  // NO CONTRADICTING-BOUNDS CASE. The only caller that passes bounds takes them from
  // `neighborBounds`, which drops any predecessor later than the upper bound, so `minDate`
  // is never after `maxDate` and the clamp can never grey out every day.
  const outOfOrder = violatesOrder(selectedDate, minDate, maxDate);

  const orderMessage = !outOfOrder
    ? undefined
    : minDate !== undefined && selectedDate! < minDate
      ? `Must be on or after ${formatDay(minDate)}${
          minLabel ? ` (${minLabel})` : ''
        }.`
      : `Must be on or before ${formatDay(maxDate as string)}${
          maxLabel ? ` (${maxLabel})` : ''
        }.`;

  const handleSave = () => {
    if (selectedDate && !outOfOrder) onSave(selectedDate);
  };

  const handleDateChange = (dates: Date[]) => {
    if (dates.length > 0 && moment(dates[0]).isValid()) {
      setSelectedDate(moment(dates[0]).format('YYYY-MM-DD'));
    }
  };

  const modalLabel = `Slot Id: ${slotId}${
    wellName ? ` | Well Name: ${wellName}` : ''
  }`;

  return (
    <Modal
      open={isOpen}
      onRequestClose={onClose}
      modalHeading={`Edit ${dateFieldLabel}`}
      modalLabel={modalLabel}
      primaryButtonText="Save"
      primaryButtonDisabled={!hasChanged || !selectedDate || outOfOrder}
      onRequestSubmit={handleSave}
      secondaryButtonText="Cancel"
      size="sm"
      className={styles.modal}
    >
      <div className={styles.modalContent}>
        <DatePicker
          id="cost-date-picker"
          labelText={dateFieldLabel}
          value={selectedDate}
          onChange={handleDateChange}
          invalid={outOfOrder}
          {...(minDate ? { minDate: pickerBound(minDate) } : {})}
          {...(maxDate ? { maxDate: pickerBound(maxDate) } : {})}
        />
        {/* RENDERED HERE, NOT THROUGH THE PICKER'S `invalidText`. Carbon's DatePicker clones
            its input child and the requirement text never reached the DOM (measured: the
            modal's entire text content carried no message), so the one thing the user needs
            to read was the one thing that did not render. `invalid` stays for the field's own
            error styling.

            `alert`, because the user's choice has just been rejected and a screen reader
            must interrupt to say so.

            ⚠️ NO `aria-describedby`, AND NOT FOR WANT OF TRYING. The review asked for the
            picker to name this text. Carbon's DatePickerInput sets that attribute itself,
            from `helperText`, AFTER spreading the caller's props —
            `['aria-describedby']: helperText ? datePickerInputHelperId : undefined`
            (@carbon/react DatePickerInput.js:122, measured) — so anything passed in is
            overwritten with undefined. Routing the text through `helperText` instead would
            get the association and lose the role, and render it twice. The live region is
            what announces; the association is what Carbon will not allow. */}
        {orderMessage && (
          <p className={styles.orderError} id={MESSAGE_ID} role="alert">
            {orderMessage}
          </p>
        )}
      </div>
    </Modal>
  );
};
